<template>
  <transition
    name="fade"
    mode="out-in"
    @beforeLeave="beforeLeave"
    @enter="enter"
    @afterEnter="afterEnter"
  >
    <section
      :id="id"
      ref="provider-search"
      class="provider-search"
      v-show="isShown"
    >
      <div class="vcore_row vcore_no-gutters">
        <form
          class="vcore_col--xl--6 provider-search_form"
          @submit.prevent="handleSearch"
        >
          <h2 class="provider-search_form_title">
            First, let’s find your provider
          </h2>
          <div class="vcore_row provider-search_form_inputs">
            <div class="vcore_col--xs--12 vcore_col--md--4">
              <Input
                size="large"
                label="ZIP Code"
                label-color="#ffffff"
                v-model="searchForm.searchZipCode"
                :mask-options="{
                  mask: [/\d/, /\d/, /\d/, /\d/, /\d/],
                  placeholderChar: ' ',
                  guide: false,
                }"
                aria-label="zip code"
                required
                @blur="$v.searchForm.searchZipCode.$touch()"
              />
            </div>
            <div class="vcore_col--xs--12 vcore_col--md--4">
              <Select
                size="large"
                label="Radius"
                label-color="#ffffff"
                v-model="searchForm.searchRadius"
                :options="radiusOptions"
                aria-label="zip code"
                required
              ></Select>
            </div>
            <div
              class="vcore_col--xs--12 vcore_col--md--4 provider-search_btn_wrapper"
            >
              <Button
                id="provider-search_btn"
                size="large"
                color="blue"
                variant="secondary"
                classes="hover_blue focus_blue"
                type="submit"
                block
              >
                Search
              </Button>
            </div>
          </div>
          <div v-if="$v.$error" class="provider-search_error_wrapper">
            <i class="vcore_icon icon-warning"></i>
            <span>Invalid ZIP Code</span>
          </div>
        </form>
        <div class="vcore_col--xl--6 provider-search_map">
          <GmapMap
            ref="map"
            :center="mapCenter"
            :zoom="4"
            :options="{
              zoomControl: true,
              mapTypeControl: false,
              scaleControl: false,
              streetViewControl: false,
              rotateControl: false,
              fullscreenControl: true,
              disableDefaultUi: false,
            }"
            style="width: 100%; height: 100%"
          >
            <GmapMarker
              v-for="(provider, index) in searchedProviders"
              :key="provider.spid + '-' + provider.location.locationID"
              :position="{
                lat: provider.location.latitude,
                lng: provider.location.longitude,
              }"
              :clickable="true"
              :draggable="false"
              @click="handleMarkerClick(index)"
              :label="markerLabel(provider)"
              :icon="markerIcon(provider)"
              :visible="showProvider(provider)"
              :zIndex="999 - index"
            >
            </GmapMarker>
            <GmapInfoWindow
              @closeclick="showMapInfoWindow = false"
              :position="{
                lat: activeProviderPosition.lat,
                lng: activeProviderPosition.lng,
              }"
              :options="{
                // content: infoWindowContent,
                pixelOffset: google && new google.maps.Size(0, -35),
              }"
              :opened="showMapInfoWindow"
            >
              <div v-if="activeMapProvider !== null">
                <div
                  v-if="
                    searchedProviders[activeMapProvider].location
                      .locationName !== ''
                  "
                >
                  <strong>
                    {{
                      searchedProviders[activeMapProvider].location.locationName
                    }}
                  </strong>
                </div>
                <div v-else>
                  <strong>
                    {{ searchedProviders[activeMapProvider].providerName }}
                  </strong>
                </div>
                <div>
                  {{ searchedProviders[activeMapProvider].location.address1 }}
                </div>
                <div
                  v-if="searchedProviders[activeMapProvider].location.address2"
                >
                  {{ searchedProviders[activeMapProvider].location.address2 }}
                </div>
                <div>
                  {{ searchedProviders[activeMapProvider].location.city }},
                  {{ searchedProviders[activeMapProvider].location.state }}
                  {{ searchedProviders[activeMapProvider].location.zipCode }}
                </div>
                <div
                  v-if="
                    !searchedProviders[activeMapProvider].selfEnrollmentEnabled
                  "
                >
                  <span class="vcore_link info-window_select-provider-link">
                    Please contact your provider directly to enroll in HSF<span
                      class="sup"
                      >&reg;</span
                    >.
                  </span>
                </div>
                <div
                  v-else-if="
                    searchedProviders[activeMapProvider]
                      .enrollmentInstitutionID !== null
                  "
                >
                  <span
                    class="vcore_link info-window_select-provider-link"
                    @click="
                      setSelectedProviderBySpid(searchedProviders[activeMapProvider])
                    "
                  >
                    Select</span
                  >
                </div>
              </div>
            </GmapInfoWindow>
          </GmapMap>
          <div
            v-if="requestStatus.searchedProviders.active"
            class="provider-search_map_loader"
          >
            <div>Searching...</div>
          </div>
          <div
            v-else-if="requestStatus.searchedProviders.error"
            class="provider-search_map_error"
          >
            <div>
              Oops.. we're having technical difficulties, please try again in a
              few minutes.
            </div>
          </div>
        </div>
      </div>
    </section>
  </transition>
</template>

<script>
import Vue from "vue";
import Uuid from "@Shared/utils/Uuid";
import Button from "@Shared/components/Button";
import Input from "@Shared/components/Input";
import Select from "@Shared/components/Select";
import { mapGetters, mapActions } from "vuex";
import { gmapApi } from "vue2-google-maps";
import {
  required,
  maxLength,
  minLength,
  numeric,
} from "vuelidate/lib/validators";
import { EventBus } from "@Shared/utils/event-bus";

export default {
  name: "ProviderSearch",
  components: {
    Button,
    Input,
    Select,
  },
  data() {
    return {
      id: "vertical-progress-flow-" + Uuid(),
      stepIndex: 0, // 0 count, must match component order in slot
      searchForm: {
        searchZipCode: "",
        searchRadius: 5,
      },
      radiusOptions: [
        {
          text: "5 miles",
          value: 5,
        },
        {
          text: "10 miles",
          value: 10,
        },
        {
          text: "15 miles",
          value: 15,
        },
        {
          text: "25 miles",
          value: 25,
        },
        {
          text: "50 miles",
          value: 50,
        },
        {
          text: "100 miles",
          value: 100,
        },
      ],
      activeMapProvider: null,
      showMapInfoWindow: false,
    };
  },
  validations: {
    searchForm: {
      searchZipCode: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(5),
        numeric,
      },
    },
  },
  computed: {
    ...mapGetters([
      "enrollmentCurrentSearchStepIndex",
      "searchedProviders",
      "selectedProvider",
      "requestStatus",
      "paginatedSearchedProviders",
    ]),

    google: gmapApi,

    isShown() {
      return this.stepIndex <= this.enrollmentCurrentSearchStepIndex;
    },

    activeProviderPosition() {
      if (
        this.searchedProviders.length > 0 &&
        this.activeMapProvider !== null
      ) {
        return {
          lat: this.searchedProviders[this.activeMapProvider].location.latitude,
          lng: this.searchedProviders[this.activeMapProvider].location
            .longitude,
        };
      }

      return { lat: 0, lng: 0 };
    },
    mapCenter() {
      if (this.searchedProviders.length > 0) {
        let total = { lat: 0, lng: 0 };
        for (let i = 0; i < this.searchedProviders.length; i++) {
          total.lat += this.searchedProviders[i].location.latitude;
          total.lng += this.searchedProviders[i].location.longitude;
        }
        total.lat /= this.searchedProviders.length;
        total.lng /= this.searchedProviders.length;

        return total;
      }
      return { lat: 39.102783, lng: -94.5829098 };
    },
  },
  watch: {
    isShown() {
      this.$nextTick(() => {
        this.updateOffsetTop();
      });
    },
    searchedProviders() {
      this.updateMapBounds();
    },
    selectedProvider(newVal) {
      if (Object.keys(newVal).length === 0 && newVal.constructor === Object) {
        this.updateMapBounds();
      } else {
        this.updateMapCenter();
      }
    },
  },
  methods: {
    ...mapActions([
      "setSearchedProviders",
      "setSelectedProviderBySpid",
      "setPageNumber",
    ]),
    isSelected(provider) {
      if (
        Object.keys(this.selectedProvider).length === 0 &&
        this.selectedProvider.constructor === Object
      ) {
        return false;
      }
      return (
        this.selectedProvider.spid === provider.spid &&
        this.selectedProvider.location.locationID ===
          provider.location.locationID
      );
    },
    showProvider(provider) {
      if (
        Object.keys(this.selectedProvider).length === 0 &&
        this.selectedProvider.constructor === Object
      ) {
        return true;
      } else {
        return this.isSelected(provider);
      }
    },
    handleSearch() {
      //Resetting steps to only 2
      if (this.verticalProgressSteps.length >= 3) {
        this.verticalProgressSteps.pop();
      }
      if (this.searchForm.searchZipCode === "") {
        this.$v.searchForm.searchZipCode.$touch();
      }
      if (!this.$v.$invalid) {
        this.setSearchedProviders(this.searchForm);
        this.setPageNumber(0);
        this.$ga.event({
          eventCategory: "Provider Search",
          eventAction: this.searchForm.searchZipCode,
          eventLabel: `${this.searchForm.searchRadius} miles`,
        });
      }
    },

    handleMarkerClick(index) {
      this.activeMapProvider = index;
      this.showMapInfoWindow = true;
    },
    updateOffsetTop() {
      Vue.set(
        this.verticalProgressSteps[this.stepIndex],
        "offsetTop",
        this.$refs["provider-search"].offsetTop
      );
    },
    markerIcon(provider) {
      if (
        this.paginatedSearchedProviders.find((p) => {
          return (
            p.spid === provider.spid &&
            p.location.locationID === provider.location.locationID
          );
        })
      ) {
        return {
          url: "../images/map-marker-active.png",
          labelOrigin: this.google && new this.google.maps.Point(16.5, 18),
        };
      }
      return {
        url: "../images/map-marker-inactive-2.png",
        labelOrigin: this.google && new this.google.maps.Point(16.5, 18),
      };
    },
    markerLabel(provider) {
      if (
        this.paginatedSearchedProviders.find((p) => {
          return (
            p.spid === provider.spid &&
            p.location.locationID === provider.location.locationID
          );
        })
      ) {
        return {
          text: provider.alphaID,
          color: "#ffffff",
          fontSize: "16px",
        };
      }
      return {
        text: provider.alphaID,
        color: "#006747",
        fontSize: "16px",
      };
    },
    updateMapBounds() {
      if (this.searchedProviders.length > 0) {
        let bounds = this.google && new this.google.maps.LatLngBounds();
        if (bounds != null) {
          for (let i = 0; i < this.searchedProviders.length; i++) {
            let location =
              this.google &&
              new this.google.maps.LatLng(
                this.searchedProviders[i].location.latitude,
                this.searchedProviders[i].location.longitude
              );
            bounds.extend(location);
          }
          // add some padding to the top, left and right
          let newNorthEast =
            this.google &&
            new this.google.maps.LatLng(
              bounds.getNorthEast().lat() + 0.001,
              bounds.getNorthEast().lng() + 0.001
            );
          let newSouthWest =
            this.google &&
            new this.google.maps.LatLng(
              bounds.getSouthWest().lat() - 0.001,
              bounds.getSouthWest().lng() + 0.001
            );
          bounds.extend(newNorthEast);
          bounds.extend(newSouthWest);

          this.$refs.map.$mapPromise.then((map) => {
            map.fitBounds(bounds);
          });
        }
      }
    },
    updateMapCenter() {
      if (
        Object.keys(this.selectedProvider).length !== 0 &&
        this.selectedProvider.constructor === Object
      ) {
        const center =
          this.google &&
          new this.google.maps.LatLng(
            this.selectedProvider.location.latitude,
            this.selectedProvider.location.longitude
          );
        this.$refs.map.$mapPromise.then((map) => {
          map.panTo(center);
        });
      }
    },

    beforeLeave(element) {
      this.prevHeight = getComputedStyle(element).height;
    },
    enter(element) {
      const { height } = getComputedStyle(element);
      element.style.height = this.prevHeight;
      setTimeout(() => {
        element.style.height = height;
      });
    },
    afterEnter(element) {
      element.style.height = "auto";
    },
  },
  inject: ["verticalProgressSteps"],
  mounted() {
    this.verticalProgressSteps.push({
      id: this.id,
      stepIndex: this.stepIndex,
      offsetTop: this.$refs["provider-search"].offsetTop,
    });
    EventBus.$on("window-resize", this.updateOffsetTop);
    EventBus.$on("update-offset", this.updateOffsetTop);

    this.updateMapBounds();
  },
  destroyed() {
    EventBus.$off("window-resize", this.updateOffsetTop);
    EventBus.$off("update-offset", this.updateOffsetTop);
  },
};
</script>

<style lang="scss" scoped>
.sup {
  font-size: 70%;
  vertical-align: super;
}
.provider-search {
  border-radius: 10px;
  overflow: hidden;

  @media (max-width: $grid-breakpoint-sm) {
    border-radius: 0;
  }

  .provider-search_form_title {
    font-weight: 600;

    @media (max-width: $grid-breakpoint-sm) {
      font-size: 20px;
      line-height: 30px;
    }
  }

  .provider-search_form {
    height: 305px;
    background-color: $color_dark-blue;
    color: #ffffff;

    @media (max-width: $grid-breakpoint-md) {
      height: auto;
    }
  }

  .provider-search_map {
    height: 305px;
    position: relative;
    .provider-search_map_loader {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 0;
      background-color: rgba($color_none-more-black, 0.6);
      overflow: hidden;
      > div {
        color: $color_white;
        text-align: center;
        position: absolute;
        top: 45%;
        width: 100%;
        font-size: 18px;
      }
    }

    .provider-search_map_error {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 0;
      background-color: rgba($color_none-more-black, 0.6);
      overflow: hidden;
      > div {
        border-left: 5px solid $color_red;
        color: $color_red;
        padding: 20px 25px;
        background-color: $color_white;
        font-size: 14px;
        line-height: 17px;
        width: 80%;
        margin: 0 auto;
        position: relative;
        top: 40%;
      }
    }

    .info-window_select-provider-link {
      color: $color_dark-blue;
      cursor: pointer;
      font-weight: bold;
    }
  }

  .vcore_no-gutters > .provider-search_form {
    padding: 60px 38px 90px;

    @media (max-width: $grid-breakpoint-md) {
      padding: 30px 25px 60px;
    }
  }

  .provider-search_form_inputs {
    /*position: relative;*/
    margin-top: 40px;
  }

  .provider-search_btn_wrapper {
    /*position: absolute;*/
    /*bottom: 0;*/
    /*right: 0;*/
    padding-top: 26px;
  }
}

.provider-search_error_wrapper {
  border-left: 5px solid $color_red;
  color: $color_red;
  padding: 10px 25px;
  background-color: $color_white;
  margin-top: 20px;
  font-size: 14px;
  i {
    font-size: 18px;
    margin-right: 7px;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.5s;
  transition-property: height, opacity;
  transition-timing-function: ease;
  overflow: hidden;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
